import { useTranslation } from "react-i18next";
import { RiCheckboxCircleFill } from "@remixicon/react";
import { Badge } from "~/components/ui/badge";
import { Picture } from "~/components/ui/picture";
import { PriceDisplay } from "~/components/ui/price-display";
import { StarRating } from "~/components/ui/star-rating";
import { FleetQuoteBuilder } from "~/components/products/pdp/FleetQuoteBuilder";
import { cn } from "~/lib/utils";
import type { FleetPricingContent } from "~/data/products/types";
import priceSlash from "~/assets/products/drive-fleet/price-slash.svg";

export interface FleetPrices {
  /** Per-device price, shown struck through. */
  unitPrice: number | null;
  subscriptionPrice: number | null;
  currencySymbol: string;
  currencyCode: string;
}

interface FleetPricingSectionProps {
  name: string;
  content: FleetPricingContent;
  prices: FleetPrices;
}

// Placeholder thumbnail slots beside the one real image, as in Figma's desktop strip.
const PLACEHOLDER_THUMBNAILS = 3;

// Fleet PDP hero (Figma 10480:407811 mobile, 10480:408667 desktop): product image,
// struck-through device price, subscription and the Fleet Quote Builder. One tree for
// both layouts; must render inside the .fleet token scope.
export function FleetPricingSection({ name, content, prices }: FleetPricingSectionProps) {
  const { t } = useTranslation("products");
  const { unitPrice, subscriptionPrice, currencySymbol, currencyCode } = prices;

  // Badge + rating. Above the image below lg, at the top of the pricing column from lg.
  const metadata = (className: string) => (
    <div className={cn("items-start justify-between", className)}>
      <Badge size="lg" className="rounded-2xl border-0 bg-[rgba(31,42,63,0.45)] font-bold tracking-[0.14px] text-[#eff4fd]">
        {t(content.tagline)}
      </Badge>
      {content.rating != null && content.reviewCount != null && (
        <div className="flex flex-col items-end">
          {/* Figma's star row: 12.4px stars on a 1.6px inset (14px tall) on mobile,
              16px stars on a 2px inset (18px tall) on desktop. */}
          <StarRating
            rating={content.rating}
            color="text-bouncie-200"
            outline
            sizeClassName="size-[12.44px] lg:size-4"
            gapClassName="gap-[3.11px] lg:gap-[1.28px]"
            className="pt-[1.56px] lg:pt-0.5"
          />
          <p className="text-text-body-secondary lg:text-text-body-alt text-xs leading-[18px]">
            {content.reviewCount.toLocaleString()} {t("pdp.reviews_suffix")}
          </p>
        </div>
      )}
    </div>
  );

  // PriceDisplay sizes from a px value, so each layout gets its own copy.
  const devicePrice = (fontSize: number, className: string) =>
    unitPrice != null && (
      <PriceDisplay
        value={unitPrice}
        currencySymbol={currencySymbol}
        currencyCode={currencyCode}
        fontSize={fontSize}
        weightClassName="font-bold"
        className={cn("text-[rgba(247,247,248,0.6)]", className)}
      />
    );

  return (
    <section className="bg-background">
      <div className="wrap flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-12 lg:pt-14 xl:justify-center xl:gap-[109px]">
        {metadata("flex pt-4 pb-2 lg:hidden")}

        <div className="flex flex-col gap-4 lg:flex-1 xl:w-[652px] xl:flex-none">
          <div className="relative flex h-[240px] items-center justify-center overflow-hidden bg-[var(--background-alt)] max-sm:-mx-4 sm:aspect-[375/240] sm:h-auto sm:rounded-2xl lg:aspect-[652/477] lg:p-2.5 xl:aspect-auto xl:h-[477px]">
            {/* Figma frames the square box shot differently per layout: 361x250 a little
                above centre on mobile (scaled with the panel from sm), zoomed to 629x450 on
                desktop. */}
            <div className="relative h-[250px] w-[361px] overflow-hidden sm:h-[104.17%] sm:w-[96.27%] lg:aspect-[629/450] lg:h-auto lg:w-full xl:w-[629px]">
              <Picture
                src={content.image.src}
                alt={t(content.image.alt)}
                width={629}
                loading="eager"
                fetchPriority="high"
                className="absolute top-[-15.65%] left-0 h-[144.4%] w-full max-w-none lg:top-[-26.84%] lg:left-[-7.95%] lg:h-[162%] lg:w-[115.9%]"
              />
            </div>
            <Badge className="text-bouncie-900 absolute top-4 left-4 rounded-2xl border-0 bg-[#b2ff0d] px-3 py-0.5 text-xs leading-5 tracking-[0.12px] lg:top-6 lg:left-6 lg:py-1 lg:text-sm lg:tracking-[0.14px]">
              {t(content.imageBadge)}
            </Badge>
          </div>

          {/* Thumbnail strip: the real image (active) plus Figma's empty slots, shown as FPO
              until more product photos exist. Hidden on phones, as in Figma's mobile frame;
              from sm it follows /products/drive's ProductImageCarousel sizes. */}
          <div aria-hidden className="hidden gap-4 self-center sm:flex lg:self-start xl:gap-6">
            <div className="size-16 overflow-hidden rounded-md outline-2 -outline-offset-2 outline-[#a9c3f4] xl:size-[85px]">
              <Picture src={content.image.src} alt="" width={85} className="size-full object-cover" />
            </div>
            {Array.from({ length: PLACEHOLDER_THUMBNAILS }, (_, i) => (
              <div
                key={i}
                className="flex size-16 items-center justify-center rounded-md bg-[var(--background-alt)] outline-[0.5px] -outline-offset-[0.5px] outline-[#f7f7f8] xl:size-[85px]"
              >
                <span className="font-display text-xs font-extrabold text-[#f201ce]">FPO</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-6 pt-2 lg:flex-1 lg:gap-8 lg:p-0 xl:w-[410px] xl:flex-none">
          <div className="flex flex-col lg:gap-14">
            {metadata("hidden lg:flex")}
            <div className="flex items-start gap-3">
              <div className="flex flex-1 flex-col gap-3">
                <h1>
                  {/* The wordmark SVG is black; masking it lets the fleet text colour fill it. */}
                  <span
                    role="img"
                    aria-label={`Bouncie ${name}`}
                    className="bg-text-body-tertiary block aspect-[143.747/48] h-[34px] mask-contain mask-no-repeat lg:h-[45px]"
                    style={{ maskImage: `url("${content.wordmark}")` }}
                  />
                </h1>
                <p className="text-text-body-secondary lg:text-md text-base leading-[1.5] lg:leading-[1.1]">
                  {t(content.productDescriptor)}
                </p>
              </div>
              {unitPrice != null && (
                <div className="relative">
                  {devicePrice(32, "lg:hidden")}
                  {devicePrice(48, "hidden lg:flex")}
                  {/* eslint-disable-next-line local/no-raw-img -- decorative SVG strike-through */}
                  <img src={priceSlash} alt="" className="pointer-events-none absolute top-[calc(50%-1px)] left-[-1px] h-[2px] w-[calc(100%+2px)]" />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-6">
            {/* Figma strokes sit inside the box, so the padding gives up the 0.5px border. */}
            <div className="flex items-center justify-between gap-2 rounded-md border-[0.5px] border-[#d6d8db] bg-[rgba(31,42,63,0.35)] p-[15.5px] lg:min-h-[73px] lg:px-[23.5px]">
              <div className="flex w-[202px] flex-col lg:w-auto lg:flex-1">
                <p className="text-text-heading lg:text-text-body text-sm leading-5 font-bold tracking-[0.14px] lg:text-base lg:tracking-[0.16px]">
                  {t(content.subscription.headline)}
                </p>
                <p className="text-text-body-secondary w-[184px] text-xs leading-[18px] lg:w-auto">{t(content.subscription.body)}</p>
              </div>
              {subscriptionPrice != null && (
                <PriceDisplay
                  value={subscriptionPrice}
                  currencySymbol={currencySymbol}
                  currencyCode={currencyCode}
                  fontSize={32}
                  weightClassName="font-bold"
                  className="text-text-heading lg:text-text-body"
                />
              )}
            </div>

            <ul className="flex flex-wrap items-center justify-center gap-2 lg:flex-nowrap lg:gap-1">
              {content.valueProps.map((key) => (
                <li key={key} className="text-text-heading flex items-center gap-1 text-sm leading-5 font-bold tracking-[0.14px] whitespace-nowrap lg:gap-0.5">
                  <span className="p-1">
                    <RiCheckboxCircleFill aria-hidden className="size-4 text-[#b2ff0d]" />
                  </span>
                  {t(key)}
                </li>
              ))}
            </ul>

            <FleetQuoteBuilder
              content={content.quoteBuilder}
              unitPrice={unitPrice}
              currencySymbol={currencySymbol}
              currencyCode={currencyCode}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
